// src/features/admin/components/settings/integrations/components/AccountingTab.tsx
import React from 'react';
import {
  CalculatorIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

const ACCOUNTING_PROVIDERS = [
  {
    name: 'QuickBooks Online',
    description: 'Popular cloud accounting with trust accounting features.',
    size: 'Small to Medium firms',
    features: ['Trust accounting', 'Invoicing', 'Time tracking', 'Bank feeds']
  },
  {
    name: 'Xero',
    description: 'Modern cloud accounting with strong automation.',
    size: 'Small to Medium firms',
    features: ['Bank reconciliation', 'Invoicing', 'Reporting', 'Multi-currency']
  },
  {
    name: 'FreshBooks',
    description: 'Simple invoicing and time tracking for solo practitioners.',
    size: 'Solo to Small firms',
    features: ['Easy invoicing', 'Time tracking', 'Expenses', 'Client portal']
  },
  {
    name: 'LEAP',
    description: 'Legal-specific practice management with integrated accounting.',
    size: 'All firm sizes',
    features: ['Trust accounting', 'Billing', 'Document automation', 'Time capture']
  },
  {
    name: 'Tabs3',
    description: 'Comprehensive legal billing and trust accounting.',
    size: 'Small to Large firms',
    features: ['Legal billing', 'Trust accounting', 'GL', 'Accounts payable']
  },
  {
    name: 'PCLaw',
    description: 'Full-featured legal accounting with Canadian compliance.',
    size: 'Medium to Large firms',
    features: ['Trust accounting', 'GL', 'Canadian compliance', 'Billing']
  }
];

const AccountingTab: React.FC = () => {
  return (
    <div className="space-y-6">
      {/* Coming Soon Banner */}
      <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-lg p-6 text-center">
        <ClockIcon className="h-12 w-12 text-amber-600 dark:text-amber-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-amber-800 dark:text-amber-200 mb-2">
          Accounting Integration - Coming in Phase 2
        </h3>
        <p className="text-sm text-amber-700 dark:text-amber-300 max-w-2xl mx-auto">
          Sync time entries, invoices, and trust accounting with your accounting software.
          This feature is scheduled for Phase 2 of development (Weeks 11-18).
        </p>
      </div>

      {/* Preview */}
      <div>
        <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <CalculatorIcon className="h-5 w-5 mr-2 text-lex-accent" />
          Planned Accounting Integrations
        </h4>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {ACCOUNTING_PROVIDERS.map((provider, index) => (
            <div
              key={index}
              className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 opacity-75"
            >
              <h5 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                {provider.name}
              </h5>
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                {provider.description}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                <strong>Best for:</strong> {provider.size}
              </p>
              <div className="flex flex-wrap gap-1">
                {provider.features.map((feature, idx) => (
                  <span
                    key={idx}
                    className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded"
                  >
                    {feature}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Sync Features */}
      <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <h5 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
          Planned Sync Features
        </h5>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
          <div className="text-center p-3 bg-white dark:bg-gray-800 rounded-lg">
            <div className="font-semibold text-gray-900 dark:text-white">Time Entries</div>
            <div className="text-gray-500">Automatic sync</div>
          </div>
          <div className="text-center p-3 bg-white dark:bg-gray-800 rounded-lg">
            <div className="font-semibold text-gray-900 dark:text-white">Invoices</div>
            <div className="text-gray-500">Create & track</div>
          </div>
          <div className="text-center p-3 bg-white dark:bg-gray-800 rounded-lg">
            <div className="font-semibold text-gray-900 dark:text-white">Trust Ledger</div>
            <div className="text-gray-500">Real-time balance</div>
          </div>
          <div className="text-center p-3 bg-white dark:bg-gray-800 rounded-lg">
            <div className="font-semibold text-gray-900 dark:text-white">Expenses</div>
            <div className="text-gray-500">Matter allocation</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountingTab;
